import { io, Socket } from 'socket.io-client';
import authService from './authService.js';
import { logger } from '../utils/logger.js';
import { env } from '../env.config.js';
import { ZodSchema } from 'zod';
import { SOCKET_EVENTS } from '../constants/socketEvents.js';

export interface SocketData {
  [key: string]: unknown;
}

/**
 * Helper for emitting custom events safely for UI layers to react (like Toasts)
 */
const dispatchSocketUIEvent = (type: 'success' | 'error' | 'info', message: string, id?: string) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('socket:ui-notification', { detail: { type, message, id } }));
  }
};

/**
 * Représente un message reçu via socket
 */
export interface SocketMessage {
  id?: string;
  type: string;
  sender?: {
    id: string;
    name?: string;
    role?: string;
  };
  content?: string;
  timestamp?: string;
  data?: Record<string, unknown>;
}

/**
 * Représente un événement d'appel
 */
export interface CallEvent {
  callId: string;
  callerId: string;
  callerName?: string;
  receiverId: string;
  type: 'video' | 'audio';
  status: 'ringing' | 'accepted' | 'declined' | 'ended';
  timestamp: string;
}

/**
 * Représente une mise à jour de salle d'attente
 */
export interface WaitingRoomUpdate {
  type: 'patient_added' | 'patient_removed' | 'priority_changed' | 'status_updated';
  patientId: string;
  teleconsultationId: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status?: string;
  timestamp: string;
}

type SocketListener<T = unknown> = (data: T) => void;
type SocketAuthPayload = {
  token?: string;
  userId?: string | number;
  socketToken?: string;
};

/**
 * Unified Socket Service - Gestion centralisée des connexions Socket.IO
 * Implémente le pattern singleton avec gestion d'état et récupération d'erreur
 */
export class UnifiedSocketService {
  private static instance: UnifiedSocketService;
  private socket: Socket | null = null;
  private connectionInProgress = false;
  private maxReconnects = 10; // Limite de reconnexions augmentée
  private reconnectCount = 0;
  private connectionStats = {
    connectionsAttempted: 0,
    connectionsSuccessful: 0,
    connectionsFailed: 0,
    lastConnectionAttempt: null as Date | null,
    lastConnectionSuccess: null as Date | null,
    totalReconnects: 0,
  };

  /**
   * Getter pour vérifier si le socket est connecté (compatibilité)
   */
  public get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Initialise le service socket sans établir de connexion
   * Prépare le service pour les connexions futures
   */
  async initialize(): Promise<void> {
    // Pour l'instant, cette méthode ne fait rien de spécial
    // Elle pourrait être utilisée pour pré-initialiser des ressources
    // ou préparer des configurations avant la connexion
    logger.debug('[UnifiedSocketService] Service initialized (no-op)');
  }

  /**
   * Établit la connexion Socket.IO avec authentification
   */
  async connect(): Promise<Socket | null> {
    if (this.connectionInProgress) {
      logger.debug('[UnifiedSocketService] Connection already in progress');
      return null;
    }

    if (this.socket?.connected) {
      logger.debug('[UnifiedSocketService] Already connected');
      return this.socket;
    }

    this.connectionInProgress = true;
    this.connectionStats.connectionsAttempted++;
    this.connectionStats.lastConnectionAttempt = new Date();

    try {
      const token = authService.getToken() || localStorage.getItem('authToken');
      const user = authService.getUser() || JSON.parse(localStorage.getItem('user_data') || '{}');

      if (!token || !user?.id) {
        logger.warn('[UnifiedSocketService] Cannot connect: missing token or user id', {
          hasToken: !!token,
          hasUserId: !!user?.id
        });
        this.connectionInProgress = false;
        return null;
      }

      // Configuration Socket.IO
      // Si SOCKET_URL est vide ou '/', on utilise window.location.origin pour le socket
      const socketUrl =
        env.SOCKET_URL ??
        (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const socketOptions = {
        auth: {
          token,
          userId: user.id,
        },
        transports: ['websocket', 'polling'], // Priorité au WebSocket pour la stabilité, fallback polling
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
        forceNew: false, // MODIFIÉ : Ne plus forcer une nouvelle connexion pour permettre le multiplexage
        // Paramètres de détection plus agressifs
        pingTimeout: 30000,
        pingInterval: 15000,
      };

      logger.debug('[UnifiedSocketService] Connecting to Socket.IO', {
        url: socketUrl,
        userId: user.id,
      });

      this.socket = io(socketUrl, socketOptions);

      // Re-attacher tous les listeners bufferisés
      this.listenersMap.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          this.socket!.on(event, callback as SocketListener<unknown>);
        });
      });
      logger.debug('[UnifiedSocketService] Re-attached all buffered listeners');

      // Attendre la connexion avec timeout généreux (Nginx proxy)
      await new Promise<void>((resolve, reject) => {
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            logger.warn(
              '[UnifiedSocketService] Connection timeout after 20s, will rely on native reconnection'
            );
            // Ne pas rejeter brutalement — on résout avec un warning
            // La reconnexion native de socket.io prendra le relais
            resolve();
          }
        }, 20000);

        this.socket!.on('connect', () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            this.connectionStats.connectionsSuccessful++;
            this.connectionStats.lastConnectionSuccess = new Date();
            this.reconnectCount = 0;
            logger.info('[UnifiedSocketService] Connected successfully', {
              socketId: this.socket?.id,
            });

            // Notification centralisée et unique
            dispatchSocketUIEvent('success', 'Connexion établie', 'socket-status');
            resolve();
          }
        });

        this.socket!.on('connect_error', (error) => {
          const errorMessage = error.message || '';
          const isAuthError =
            errorMessage.includes('Authentication failed') ||
            errorMessage.includes('TOKEN_EXPIRED') ||
            errorMessage.includes('TOKEN_INVALID');

          logger.warn('[UnifiedSocketService] Connection error (will retry):', {
            message: errorMessage,
            isAuthError,
            socketId: this.socket?.id,
            url: socketUrl,
          });

          // Si c'est une erreur d'authentification, on stoppe la reconnexion automatique
          if (isAuthError) {
            logger.warn(
              '[UnifiedSocketService] Authentication failed persistently. Stopping reconnection.'
            );
            this.socket?.disconnect();

            if (errorMessage.includes('TOKEN_EXPIRED') && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('auth:expired'));
            }

            // Seules les erreurs d'auth rejettent la promesse
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              reject(error);
            }
          }
          // Les autres erreurs (réseau, CORS) sont gérées par la reconnexion native
          // On ne rejette PAS la promesse pour éviter les Uncaught (in promise)
        });

        // Écouteurs pour la reconnexion native
        this.socket!.on('reconnect_attempt', (attempt) => {
          logger.debug(`[UnifiedSocketService] Native reconnect attempt #${attempt}: pulling fresh token from storage`);
          const freshToken = authService.getToken();
          if (freshToken) {
            if (typeof this.socket!.auth === 'object' && this.socket!.auth !== null) {
              (this.socket!.auth as SocketAuthPayload).token = freshToken;
            } else {
              this.socket!.auth = { token: freshToken };
            }
          }
        });

        this.socket!.on('reconnect', (attempt) => {
          logger.info(`[UnifiedSocketService] Reconnected successfully after ${attempt} attempts`);
          this.reconnectCount = 0;
          dispatchSocketUIEvent('success', 'Connexion rétablie', 'socket-status');
        });

        this.socket!.on('reconnect_failed', () => {
          logger.error('[UnifiedSocketService] Native reconnection failed after all attempts');
        });

        this.socket!.on('disconnect', (reason) => {
          logger.warn('[UnifiedSocketService] Socket disconnected', {
            reason,
            socketId: this.socket?.id,
          });

          if (reason !== 'io client disconnect') {
            dispatchSocketUIEvent('error', 'Connexion perdue, reconnexion en cours...', 'socket-status');
          }

          if (reason === 'io server disconnect') {
            logger.info(
              '[UnifiedSocketService] Server forced disconnect, attempting manual reconnect'
            );
            this.connect().catch(() => {});
          }
        });

        // Gestion de l'authentification réussie (pour re-sync)
        this.socket!.on(SOCKET_EVENTS.AUTHENTICATED, () => {
          if (this.connectionStats.connectionsSuccessful > 1) {
            logger.info(
              '[UnifiedSocketService] Re-authenticated after reconnect, triggering re-sync'
            );
            // On peut utiliser un CustomEvent ou simplement exposer une méthode onReconnected
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('socket:reconnected'));
            }
          }
        });
      });

      logger.info('[UnifiedSocketService] Connection setup complete', {
        socketId: this.socket?.id,
      });
      return this.socket;
    } catch (error) {
      this.connectionStats.connectionsFailed++;
      logger.error('[UnifiedSocketService] Connection failed:', error);
      this.socket = null;
      throw error;
    } finally {
      this.connectionInProgress = false;
    }
  }

  /**
   * Déconnecte le socket proprement
   */
  disconnect(): void {
    if (this.socket) {
      logger.debug('[UnifiedSocketService] Disconnecting socket');
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionInProgress = false;
  }

  /**
   * Vérifie si le socket est connecté
   */
  isSocketConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Vérifie si une connexion est en cours
   */
  isConnectionInProgress(): boolean {
    return this.connectionInProgress;
  }

  /**
   * Retourne l'instance du socket
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Émet un événement avec gestion d'erreur
   */
  emit(event: string, data?: SocketData): void {
    if (!this.socket?.connected) {
      logger.warn('[UnifiedSocketService] Cannot emit: socket not connected', { event });
      return;
    }

    try {
      this.socket.emit(event, data);
      logger.debug('[UnifiedSocketService] Event emitted', { event, hasData: !!data });
    } catch (error) {
      logger.error('[UnifiedSocketService] Failed to emit event', { event, error });
    }
  }

  private listenersMap: Map<string, Set<SocketListener>> = new Map();

  /**
   * Écoute un événement
   */
  on<T = unknown>(event: string, callback: SocketListener<T>): void {
    if (!this.listenersMap.has(event)) {
      this.listenersMap.set(event, new Set());
    }
    this.listenersMap.get(event)!.add(callback as SocketListener);

    if (this.socket) {
      this.socket.on(event, callback as SocketListener);
      logger.debug('[UnifiedSocketService] Event listener added immediately', { event });
    } else {
      logger.debug('[UnifiedSocketService] Event listener buffered for later', { event });
    }
  }

  /**
   * Écoute un événement avec validation Zod
   */
  validatedOn<T>(event: string, schema: ZodSchema<T>, callback: (data: T) => void): void {
    this.on(event, (data) => {
      const result = schema.safeParse(data);
      if (result.success) {
        callback(result.data);
      } else {
        logger.error(`[UnifiedSocketService] Invalid payload for event ${event}`, {
          error: result.error,
          data,
        });
      }
    });
  }

  /**
   * Écoute les événements de message
   */
  onMessage(callback: (...args: unknown[]) => void): void {
    this.on('message', callback);
  }

  /**
   * Écoute les événements de salle d'attente
   */
  onWaitingRoomUpdate(callback: (...args: unknown[]) => void): void {
    // Écouter les événements standardisés via les constantes
    this.on(SOCKET_EVENTS.WAITING_QUEUE_UPDATED, callback);
    this.on(SOCKET_EVENTS.QUEUE_UPDATED, callback);
    this.on(SOCKET_EVENTS.NEW_PATIENT_WAITING, callback);
    this.on(SOCKET_EVENTS.PATIENT_LEFT_QUEUE, callback);

    // Garder compatibilité temporaire pour d'anciens événements potentiels
    this.on('waiting_room:update', callback);
    this.on('waiting_room:updated', callback);
  }

  /**
   * Écoute le handshake de consultation (tokens Agora)
   */
  onConsultationHandshake(callback: SocketListener<Record<string, unknown>>): void {
    this.on(SOCKET_EVENTS.CONSULTATION_HANDSHAKE, callback);
  }

  /**
   * Écoute le démarrage effectif de la consultation
   */
  onConsultationStarted(callback: SocketListener<Record<string, unknown>>): void {
    this.on(SOCKET_EVENTS.CONSULTATION_STARTED, callback);
  }

  /**
   * Met à jour la priorité d'un patient dans la salle d'attente
   */
  updatePatientPriority(
    teleconsultationId: string | number,
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  ): void {
    this.emit('waiting_room:priority_update', {
      teleconsultationId,
      priority,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Écoute les événements d'appel
   */
  onCallEvent(callback: (...args: unknown[]) => void): void {
    this.on('call:event', callback);
  }

  /**
   * Écoute les mises à jour des statistiques admin
   */
  onAdminStatsUpdate<T = unknown>(callback: SocketListener<T>): void {
    this.on<T>('admin:stats_update', callback);
  }

  /**
   * Écoute les mises à jour de services admin
   */
  onAdminServiceUpdate<T = unknown>(callback: SocketListener<T>): void {
    this.on<T>('admin:service_update', callback);
  }

  /**
   * Écoute les nouvelles activités récentes admin
   */
  onAdminRecentActivity<T = unknown>(callback: SocketListener<T>): void {
    this.on<T>('admin:recent_activity', callback);
  }

  /**
   * Arrête d'écouter les mises à jour des statistiques admin
   */
  offAdminStatsUpdate<T = unknown>(callback?: SocketListener<T>): void {
    this.off<T>('admin:stats_update', callback);
  }

  /**
   * Arrête d'écouter les mises à jour de services admin
   */
  offAdminServiceUpdate<T = unknown>(callback?: SocketListener<T>): void {
    this.off<T>('admin:service_update', callback);
  }

  /**
   * Arrête d'écouter les nouvelles activités récentes admin
   */
  offAdminRecentActivity<T = unknown>(callback?: SocketListener<T>): void {
    this.off<T>('admin:recent_activity', callback);
  }

  /**
   * Écoute les mises à jour des constantes vitales (vitals)
   */
  onVitalsUpdate(callback: SocketListener<Record<string, unknown>>): void {
    this.on(SOCKET_EVENTS.HEALTH_METRICS_UPDATED, callback);
  }

  /**
   * Écoute l'événement de connexion (compatibilité avec anciens hooks)
   */
  onConnected(callback: () => void): void {
    this.on('connect', callback);
  }

  // Méthodes d'appels (ajoutées pour compatibilité avec useSocket)
  startCall(receiverId: string, type: 'video' | 'audio'): void {
    this.emit('call:start', { receiverId, type });
  }

  acceptCall(callId: string): void {
    this.emit('call:accept', { callId });
  }

  rejectCall(callId: string): void {
    this.emit('call:reject', { callId });
  }

  endCall(callId: string): void {
    this.emit('call:end', { callId });
  }

  // Méthodes d'authentification (compatibilité avec useSocketAuth)
  setAuthToken(token: string): void {
    if (this.socket) {
      if (typeof this.socket.auth === 'object' && this.socket.auth !== null) {
        (this.socket.auth as SocketAuthPayload).token = token;
      } else {
        this.socket.auth = { token };
      }
      if (!this.socket.connected) {
        logger.info('[UnifiedSocketService] New token set while socket disconnected, triggering reconnect');
        this.socket.connect();
      }
    }
  }

  setSocketCredentials(jwt: string, socketToken: string): void {
    if (this.socket) {
      if (typeof this.socket.auth === 'object' && this.socket.auth !== null) {
        const authPayload = this.socket.auth as SocketAuthPayload;
        authPayload.token = jwt;
        authPayload.socketToken = socketToken;
      } else {
        this.socket.auth = { token: jwt, socketToken };
      }
      if (!this.socket.connected) {
        logger.info('[UnifiedSocketService] New credentials set while socket disconnected, triggering reconnect');
        this.socket.connect();
      }
    }
  }

  /**
   * Rejoint une conversation spécifique
   */
  joinConversation(conversationId: string | number): void {
    this.emit('join_conversation', { conversationId: String(conversationId) });
    logger.debug('[UnifiedSocketService] Joined conversation', { conversationId });
  }

  /**
   * Quitte une conversation spécifique
   */
  leaveConversation(conversationId: string | number): void {
    this.emit('leave_conversation', { conversationId: String(conversationId) });
    logger.debug('[UnifiedSocketService] Left conversation', { conversationId });
  }

  /**
   * Émet un événement de "en train de taper"
   */
  emitTyping(conversationId: string | number, isTyping: boolean): void {
    this.emit('typing', {
      conversationId: String(conversationId),
      isTyping,
    });
  }

  /**
   * Arrête d'écouter un événement
   */
  off<T = unknown>(event: string, callback?: SocketListener<T>): void {
    if (callback) {
      this.listenersMap.get(event)?.delete(callback as SocketListener);
    } else {
      this.listenersMap.delete(event);
    }

    if (!this.socket) {
      logger.warn('[UnifiedSocketService] Cannot remove listener: socket not initialized', {
        event,
      });
      return;
    }

    if (callback) {
      this.socket.off(event, callback as SocketListener<unknown>);
    } else {
      this.socket.off(event);
    }

    logger.debug('[UnifiedSocketService] Event listener removed', { event });
  }

  /**
   * Arrête d'écouter l'événement de connexion (compatibilité avec anciens hooks)
   */
  offConnected(callback?: () => void): void {
    this.off('connect', callback);
  }
  /**
   * Retourne les statistiques de connexion
   */
  getConnectionStats() {
    return {
      ...this.connectionStats,
      isConnected: this.isSocketConnected(),
      isConnecting: this.connectionInProgress,
      socketId: this.socket?.id ?? null,
      connected: this.socket?.connected ?? false,
      reconnectCount: this.reconnectCount,
      maxReconnects: this.maxReconnects,
    };
  }

  /**
   * Force une reconnexion (utilisé par SocketManager)
   */
  async reconnect(): Promise<Socket | null> {
    if (this.reconnectCount >= this.maxReconnects) {
      logger.warn(
        `[UnifiedSocketService] Max reconnects (${this.maxReconnects}) reached, aborting`
      );
      return null;
    }

    this.reconnectCount++;
    this.connectionStats.totalReconnects++;

    logger.debug(
      `[UnifiedSocketService] Forced reconnect (${this.reconnectCount}/${this.maxReconnects})`
    );
    this.disconnect();
    return this.connect();
  }

  /**
   * Retourne l'instance unique du service (singleton)
   */
  public static getInstance(): UnifiedSocketService {
    if (!UnifiedSocketService.instance) {
      UnifiedSocketService.instance = new UnifiedSocketService();
    }
    return UnifiedSocketService.instance;
  }
}

// Singleton instance
const socketService = UnifiedSocketService.getInstance();

export { socketService };
export default socketService;
